import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export interface HoverPopoverProps {
  /** The trigger element (e.g., an info icon) */
  trigger: React.ReactNode;
  /** The content to display in the popover */
  content: React.ReactNode;
  /** Position of the popover relative to trigger (default: 'right') */
  position?: 'right' | 'left' | 'top' | 'bottom';
  /** Offset from trigger in pixels (default: 8) */
  offset?: number;
  /** Width of the popover (default: '18rem' / 288px) */
  width?: string;
  /** Additional CSS class for the trigger container */
  className?: string;
  /** Hover delay before showing popover in ms (default: 0) */
  showDelay?: number;
  /** Hover delay before hiding popover in ms (default: 200) */
  hideDelay?: number;
  /** If true, popover stays visible when hovering over it (default: false) */
  persistent?: boolean;
  /** Maximum height before scrolling (default: '32rem' / 512px) */
  maxHeight?: string;
}

/**
 * Reusable hover-triggered popover component
 * Based on the pattern from TableFilterSidebar.tsx
 *
 * Features:
 * - Portal-based rendering for proper z-index layering
 * - Configurable positioning
 * - Smooth hover transitions with delay
 * - Automatic positioning based on trigger element
 * - Persistent mode: popover stays visible when hovering over it
 * - Scrollable content with configurable max height
 */
export const HoverPopover: React.FC<HoverPopoverProps> = ({
  trigger,
  content,
  position = 'right',
  offset = 8,
  width = '18rem',
  className = '',
  showDelay = 0,
  hideDelay = 200,
  persistent = false,
  maxHeight = '32rem'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [isHoveringPopover, setIsHoveringPopover] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const handleMouseEnter = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (showDelay > 0) {
      const id = window.setTimeout(() => {
        setIsVisible(true);
        setTimeoutId(null);
      }, showDelay);
      setTimeoutId(id);
    } else {
      setIsVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    // In persistent mode, don't hide if user is hovering over the popover
    if (persistent && isHoveringPopover) {
      return;
    }

    const id = window.setTimeout(() => {
      setIsVisible(false);
      setTimeoutId(null);
    }, hideDelay);
    setTimeoutId(id);
  };

  const handlePopoverMouseEnter = () => {
    if (!persistent) return;

    setIsHoveringPopover(true);
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const handlePopoverMouseLeave = () => {
    if (!persistent) return;

    setIsHoveringPopover(false);
    const id = window.setTimeout(() => {
      setIsVisible(false);
      setTimeoutId(null);
    }, hideDelay);
    setTimeoutId(id);
  };

  const calculatePosition = (): React.CSSProperties => {
    if (!triggerRef.current) return {};

    const rect = triggerRef.current.getBoundingClientRect();

    switch (position) {
      case 'right':
        return {
          left: `${rect.right + offset}px`,
          top: `${rect.top}px`,
        };
      case 'left':
        return {
          right: `${window.innerWidth - rect.left + offset}px`,
          top: `${rect.top}px`,
        };
      case 'top':
        return {
          left: `${rect.left}px`,
          bottom: `${window.innerHeight - rect.top + offset}px`,
        };
      case 'bottom':
        return {
          left: `${rect.left}px`,
          top: `${rect.bottom + offset}px`,
        };
      default:
        return {
          left: `${rect.right + offset}px`,
          top: `${rect.top}px`,
        };
    }
  };

  return (
    <div
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {trigger}

      {isVisible && triggerRef.current && createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[10500]"
          style={{
            ...calculatePosition(),
            width,
          }}
          onMouseEnter={persistent ? handlePopoverMouseEnter : handleMouseEnter}
          onMouseLeave={persistent ? handlePopoverMouseLeave : handleMouseLeave}
        >
          <div
            className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm overflow-y-auto"
            style={{ maxHeight }}
          >
            {content}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
